import { useEffect, useRef, useState } from "react";
import {
  ChevronIcon,
  CloseIcon,
  FileIcon,
  ReloadIcon,
  WarningIcon,
} from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import type {
  UploadFileItem,
  UploadState,
} from "../../hooks/useDataRoomUpload";

import type { RejectedFile } from "./utils/dropUtils";

type UploadProgressPanelProps = {
  uploadState: UploadState;
  rejectedFiles: RejectedFile[];
  onRemoveItem: (nodeId: string) => void;
  onRetryFailed: () => void;
  onRetrySingle: (nodeId: string) => void;
  onUnmount: () => void;
};

type StatusIndicatorProps = {
  status: UploadFileItem["status"];
  isReplacement?: boolean;
};

function StatusIndicator({ status, isReplacement }: StatusIndicatorProps) {
  switch (status) {
    case "pending":
      return (
        <>
          <span
            aria-hidden="true"
            className="size-2 rounded-full bg-border/primary"
            data-testid="upload-status-pending"
          />
          <span className="sr-only">
            {m.coInvestments_dataRoom_uploadStatus_pending()}
          </span>
        </>
      );
    case "uploading":
      return (
        <>
          <span
            aria-hidden="true"
            className="size-2 animate-spin rounded-full border border-brand-default border-t-transparent"
            data-testid="upload-status-uploading"
          />
          <span className="sr-only">
            {isReplacement
              ? m.coInvestments_dataRoom_uploadStatus_replacing()
              : m.coInvestments_dataRoom_uploadStatus_uploading()}
          </span>
        </>
      );
    case "done":
      return (
        <>
          <span
            aria-hidden="true"
            className="size-2 rounded-full bg-positive-default"
            data-testid="upload-status-done"
          />
          <span className="sr-only">
            {m.coInvestments_dataRoom_uploadStatus_done()}
          </span>
        </>
      );
    case "error":
      return (
        <>
          <WarningIcon
            aria-hidden="true"
            className="size-3 text-negative-default"
            data-testid="upload-status-error"
          />
          <span className="sr-only">
            {m.coInvestments_dataRoom_uploadStatus_error()}
          </span>
        </>
      );
  }
}

function getHeaderText(uploadState: UploadState): string {
  if (uploadState.isUploading) {
    return m.coInvestments_dataRoom_uploadingFiles({
      completed: uploadState.completedCount,
      total: uploadState.totalCount,
    });
  }

  if (uploadState.hasErrors) {
    return m.coInvestments_dataRoom_uploadErrors({
      count: uploadState.items.filter((i) => i.status === "error").length,
    });
  }

  return m.coInvestments_dataRoom_uploadComplete({
    count: uploadState.totalCount,
  });
}

export function UploadProgressPanel({
  uploadState,
  rejectedFiles,
  onRemoveItem,
  onRetryFailed,
  onRetrySingle,
  onUnmount,
}: UploadProgressPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const onUnmountRef = useRef(onUnmount);
  onUnmountRef.current = onUnmount;

  useEffect(() => () => onUnmountRef.current(), []);

  return (
    <div
      className="border-b border-border/primary bg-background/primary"
      data-testid="upload-progress-panel"
    >
      <div className="flex items-center gap-2 px-4 py-2">
        <ThemedIconButton
          aria-expanded={isExpanded}
          ariaLabel={
            isExpanded
              ? m.coInvestments_dataRoom_collapseUploadPanel()
              : m.coInvestments_dataRoom_expandUploadPanel()
          }
          className="h-4 w-4 shrink-0"
          dataTestId="upload-progress-toggle"
          icon={
            <ChevronIcon
              className={cnTwMerge(
                "size-2.5 transition-transform duration-150",
                isExpanded ? "rotate-0" : "-rotate-90",
              )}
            />
          }
          onClick={() => setIsExpanded((prev) => !prev)}
          size="extraSmall"
          variant="noBackground"
        />
        <span className="body-small-medium flex-1 text-text/primary">
          {getHeaderText(uploadState)}
        </span>
        {uploadState.hasErrors ? (
          <ThemedButton
            dataTestId="upload-retry-all"
            onClick={onRetryFailed}
            size="small"
            variant="text"
          >
            {m.coInvestments_dataRoom_retryAll()}
          </ThemedButton>
        ) : null}
      </div>
      {isExpanded ? (
        <div className="max-h-40 overflow-y-auto px-4 pb-2">
          {uploadState.items.map((item) => (
            <div
              className="flex items-center gap-2 py-1"
              data-testid={`upload-item-${item.nodeId}`}
              key={item.nodeId}
            >
              <StatusIndicator
                isReplacement={item.isReplacement}
                status={item.status}
              />
              <FileIcon
                aria-hidden="true"
                className="size-3 shrink-0 text-icon/primary"
              />
              <span className="body-small-regular flex-1 truncate text-text/primary">
                {item.fileName}
              </span>
              {item.isReplacement &&
              (item.status === "pending" || item.status === "uploading") ? (
                <span
                  className="body-small-regular text-text/secondary"
                  data-testid={`upload-item-replacing-${item.nodeId}`}
                >
                  {m.coInvestments_dataRoom_uploadStatus_replacing()}
                </span>
              ) : null}
              {item.status === "error" ? (
                <>
                  <ThemedIconButton
                    ariaLabel={m.coInvestments_dataRoom_retryUploadItem({
                      fileName: item.fileName,
                    })}
                    className="h-4 w-4"
                    dataTestId={`upload-retry-${item.nodeId}`}
                    icon={<ReloadIcon className="size-3" />}
                    onClick={() => onRetrySingle(item.nodeId)}
                    size="extraSmall"
                    title={item.error}
                    variant="noBackground"
                  />
                  <ThemedIconButton
                    ariaLabel={m.coInvestments_dataRoom_removeUploadItem({
                      fileName: item.fileName,
                    })}
                    className="h-4 w-4"
                    dataTestId={`upload-remove-${item.nodeId}`}
                    icon={<CloseIcon className="size-3" />}
                    onClick={() => onRemoveItem(item.nodeId)}
                    size="extraSmall"
                    variant="noBackground"
                  />
                </>
              ) : null}
            </div>
          ))}
          {rejectedFiles.map((file) => (
            <div
              className="flex items-center gap-2 py-1"
              key={`rejected-${file.name}-${file.reason}`}
            >
              <WarningIcon
                aria-hidden="true"
                className="size-3 shrink-0 text-negative-default"
              />
              <FileIcon
                aria-hidden="true"
                className="size-3 shrink-0 text-icon/primary"
              />
              <span className="body-small-regular flex-1 truncate text-text/secondary">
                {file.name}
              </span>
              <span className="body-small-regular text-text/secondary">
                {file.reason === "size"
                  ? m.coInvestments_dataRoom_fileTooLarge()
                  : m.coInvestments_dataRoom_fileTypeNotAllowed()}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
